import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { OrderLine } from '@api/Orders/OrderLine';
import { getOrderLineDisplay } from '@utils/Order';
import { Customer } from '@api/Customers/Customer';
import logo from '@assets/logos/logo.png';
import { currencyFormatter } from '@utils/Numbers';

// EL LIENZO del recibo: componente PURO, lo único que se exporta como imagen
// (PLAN-REDISENO-PANEL-POS-Y-RECIBO, D6). Su destino real es mandarse por
// WhatsApp, así que tiene que verse bien como IMAGEN, no como diálogo.
//
// ⚠️ ANCHO FIJO a propósito. Antes el ticket heredaba el ancho del diálogo, así
// que la imagen salía de distinto tamaño según la pantalla del dispositivo que la
// generaba. Con `TICKET_WIDTH` la imagen es idéntica en cualquier tablet.
//
// ⚠️ SIN TÍTULO DE MODO (ajuste de Irving, 2026-08-27): la primera versión ponía
// "Recibo" o "Confirma tu pedido" según hubiera folio, y un pie distinto para
// cada caso. **Es UN ticket y se manda para muchas cosas**, así que no se
// etiqueta: lo único que cambia es qué DATOS existen (el pre-cobro no tiene folio
// ni pagos todavía, y simplemente no se pintan).
export const TICKET_WIDTH = 420;

export interface TicketImageProps {
    customer?: Customer;
    products: OrderLine[];
    subtotal: number;
    promotion: number;
    shippingCost: number;
    points: number;
    total: number;
    // Fecha a mostrar. Sin prop = ahora (recibo pre-cobro); la re-impresión desde
    // el tablero pasa la fecha REAL de la orden.
    date?: Date;
    // --- Todo lo de abajo es OPCIONAL: el pre-cobro no lo tiene ---
    shortId?: string;
    branchOfficeName?: string;
    channelLabel?: string;
    tipAmount?: number;
    cashAmount?: number;
    creditCardAmount?: number;
    bankTransferAmount?: number;
}

const money = (amount: number) => currencyFormatter(amount);

// Renglón de dinero: etiqueta a la izquierda, importe a la derecha, SIEMPRE en una
// sola línea. Antes el precio iba en un `Grid item xs={1}` (~50 px) y un $228.00
// no cabía: era la causa principal de que el recibo se viera roto.
const MoneyRow = ({
    label,
    amount,
    strong,
    muted
}: {
    label: string;
    amount: string;
    strong?: boolean;
    muted?: boolean;
}) => (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
        <Typography
            variant="body2"
            fontWeight={strong ? 800 : 500}
            color={muted ? 'text.secondary' : 'text.primary'}
            // El TOTAL es la línea que el cliente busca: se distingue por tamaño,
            // no solo por negritas (a 0.95rem se perdía entre los demás renglones)
            sx={strong ? { fontSize: '1.25rem' } : undefined}
        >
            {label}
        </Typography>
        <Typography
            variant="body2"
            fontWeight={strong ? 800 : 600}
            color={muted ? 'text.secondary' : 'text.primary'}
            whiteSpace="nowrap"
            sx={strong ? { fontSize: '1.25rem' } : undefined}
        >
            {amount}
        </Typography>
    </Stack>
);

export const TicketImage = React.forwardRef<HTMLDivElement, TicketImageProps>((props, ref) => {
    const {
        customer,
        products,
        subtotal,
        promotion,
        shippingCost,
        points,
        total,
        date,
        shortId,
        branchOfficeName,
        channelLabel,
        tipAmount = 0,
        cashAmount = 0,
        creditCardAmount = 0,
        bankTransferAmount = 0
    } = props;

    const when = date || new Date();
    const customerName = customer?.profile?.name;
    // Los pagos solo existen cuando la orden ya existe; si no llegan, no se pintan
    const hasPayments = cashAmount > 0 || creditCardAmount > 0 || bankTransferAmount > 0;

    return (
        <Box ref={ref} sx={{ width: TICKET_WIDTH, bgcolor: '#FFFFFF', p: 3 }}>
            <Stack spacing={1.5}>
                {/* ---------- Encabezado ---------- */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box component="img" src={logo} alt="Krispis" sx={{ height: 40 }} />
                    {/* El folio, cuando existe. Sin etiqueta de documento. */}
                    {shortId && (
                        <Typography variant="body2" fontWeight={700} whiteSpace="nowrap">
                            {shortId}
                        </Typography>
                    )}
                </Stack>

                <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Typography variant="caption" color="text.secondary">
                        {[branchOfficeName, channelLabel].filter(Boolean).join(' · ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                        {format(when, "dd/MM/yyyy · HH:mm", { locale: es })}
                    </Typography>
                </Stack>

                {customerName && customerName !== '0000' && (
                    <>
                        <Divider />
                        <Stack>
                            <Typography variant="body1" fontWeight={700}>
                                {customerName}
                            </Typography>
                            {customer?.phone && (
                                <Typography variant="body2" color="text.secondary">
                                    {customer.phone}
                                </Typography>
                            )}
                        </Stack>
                    </>
                )}

                <Divider />

                {/* ---------- Productos ---------- */}
                <Stack spacing={1.25}>
                    {products.map((cartLine, index) => {
                        const line = getOrderLineDisplay(cartLine);
                        return (
                            <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                                <Typography variant="body2" fontWeight={700} sx={{ width: 28, flexShrink: 0 }}>
                                    {`${line.quantity}x`}
                                </Typography>
                                <Box flex={1} minWidth={0}>
                                    <Typography variant="body2" fontWeight={700}>
                                        {line.name}
                                    </Typography>
                                    {line.modifiers.map((modifier, idx) => (
                                        <Typography key={idx} variant="caption" color="text.secondary" display="block">
                                            + {modifier.quantity}x {modifier.optionName || modifier.label}
                                            {modifier.extraCostTotal > 0
                                                ? ` (+${money(modifier.extraCostTotal)})`
                                                : ''}
                                        </Typography>
                                    ))}
                                    {line.removedIngredients.length > 0 && (
                                        <Typography variant="caption" color="error" display="block">
                                            Sin: {line.removedIngredients.join(', ')}
                                        </Typography>
                                    )}
                                    {line.note && (
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            fontStyle="italic"
                                            color="text.secondary"
                                        >
                                            Nota: {line.note}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography variant="body2" fontWeight={700} whiteSpace="nowrap">
                                    {money(line.total)}
                                </Typography>
                            </Stack>
                        );
                    })}
                </Stack>

                <Divider />

                {/* ---------- La cuenta ---------- */}
                <Stack spacing={0.5}>
                    <MoneyRow label="Subtotal" amount={money(subtotal)} />
                    {promotion > 0 && <MoneyRow label="Promoción" amount={`-${money(promotion)}`} />}
                    {shippingCost > 0 && <MoneyRow label="Envío" amount={money(shippingCost)} />}
                    {points > 0 && <MoneyRow label="Puntos" amount={`-${money(points)}`} />}
                    <Divider sx={{ my: 0.5 }} />
                    <MoneyRow label="Total" amount={money(total)} strong />
                    {tipAmount > 0 && <MoneyRow label="Propina" amount={money(tipAmount)} />}
                </Stack>

                {/* ---------- Cómo se pagó (solo cuando la orden ya existe) ---------- */}
                {hasPayments && (
                    <>
                        <Divider />
                        <Stack spacing={0.5}>
                            {cashAmount > 0 && <MoneyRow label="Efectivo" amount={money(cashAmount)} muted />}
                            {creditCardAmount > 0 && (
                                <MoneyRow label="Tarjeta" amount={money(creditCardAmount)} muted />
                            )}
                            {bankTransferAmount > 0 && (
                                <MoneyRow label="Transferencia" amount={money(bankTransferAmount)} muted />
                            )}
                        </Stack>
                    </>
                )}

                {/* ---------- Pie ---------- */}
                <Divider />
                <Stack alignItems="center" spacing={0.25}>
                    <Typography variant="body2" fontWeight={700}>
                        ¡Gracias por tu preferencia!
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        krispis.mx
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
});

TicketImage.displayName = 'TicketImage';
